/**
 * Organization Business Rules Hook
 * 
 * Manages customizable business rules and policies for organizations. These rules
 * define minimum requirements for vendor engagement, RFQ targeting, and job completion
 * workflows, allowing organizations to enforce their procurement standards automatically.
 * 
 * **Rule Categories:**
 * 
 * **RFQ Rules:**
 * - Minimum number of vendors that must be invited
 * - Minimum vendor verification level (none, basic, enhanced, premium)
 * - Minimum vendor rating threshold
 * - Minimum completed jobs requirement
 * - Required certifications for vendor participation
 * 
 * **Job & Award Rules:**
 * - Auto-accept completion after specified days
 * - Default payment terms
 * - Review requirements
 * 
 * **Key Features:**
 * - Fetch organization-specific business rules
 * - Update/upsert rules (admin only)
 * - Type-safe rule validation
 * - Automatic cache invalidation
 * 
 * **Use Cases:**
 * - Enforce quality standards in vendor selection
 * - Automate vendor eligibility filtering
 * - Standardize procurement workflows
 * - Compliance and policy enforcement
 * 
 * @param {string | undefined} organizationId - Organization ID to fetch rules for
 * 
 * @returns {Object} Business rules state
 * @returns {OrganizationBusinessRules | null} rules - Current business rules
 * @returns {boolean} isLoading - Loading state
 * @returns {Function} updateRules - Update business rules
 * @returns {boolean} isUpdating - Whether update is in progress
 * 
 * @example
 * // Fetch and display org rules
 * const { rules, isLoading } = useOrganizationBusinessRules(orgId);
 * 
 * if (rules) {
 *   console.log(`Minimum vendors: ${rules.minimum_vendors_invited}`);
 *   console.log(`Required verification: ${rules.minimum_vendor_verification_level}`);
 * }
 * 
 * @example
 * // Update business rules
 * const { updateRules } = useOrganizationBusinessRules(orgId);
 * 
 * updateRules({
 *   minimum_vendors_invited: 3,
 *   minimum_vendor_verification_level: 'enhanced',
 *   minimum_vendor_rating: 4.5,
 *   auto_accept_completion_days: 30
 * });
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface OrganizationBusinessRules {
  id?: string;
  organization_id: string;
  // RFQ Rules
  minimum_vendors_invited: number;
  minimum_vendor_verification_level: 'none' | 'basic' | 'enhanced' | 'premium';
  minimum_vendor_rating: number;
  minimum_vendor_completed_jobs: number;
  required_certification_ids: string[];
  // Job & Award Rules
  auto_accept_completion_days: number;
}

export const useOrganizationBusinessRules = (organizationId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: rules, isLoading } = useQuery({
    queryKey: ['organization-business-rules', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from('organization_business_rules')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;
      
      // Cast the verification level to the correct type
      if (data) {
        return {
          ...data,
          minimum_vendor_verification_level: data.minimum_vendor_verification_level as 'none' | 'basic' | 'enhanced' | 'premium',
        } as OrganizationBusinessRules;
      }
      
      return data;
    },
    enabled: !!organizationId,
  });

  const upsertRules = useMutation({
    mutationFn: async (rules: Partial<OrganizationBusinessRules>) => {
      if (!organizationId) throw new Error('Organization ID required');

      const { data, error } = await supabase
        .from('organization_business_rules')
        .upsert({
          ...rules,
          organization_id: organizationId,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Cast the verification level to the correct type
      if (data) {
        return {
          ...data,
          minimum_vendor_verification_level: data.minimum_vendor_verification_level as 'none' | 'basic' | 'enhanced' | 'premium',
        } as OrganizationBusinessRules;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-business-rules', organizationId] });
      toast.success('Business rules updated successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to update business rules:', error);
      toast.error('Failed to update business rules: ' + error.message);
    },
  });

  return {
    rules,
    isLoading,
    updateRules: upsertRules.mutate,
    isUpdating: upsertRules.isPending,
  };
};
